import { useEffect } from 'react';

import { useMeasure } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { PieChart } from '@mui/x-charts/PieChart';

import { shadeHexColor } from '@/utils';

import {
  EnergyUsageByModel,
  useBreakpoints,
  useFormatters,
  usePostEnergyUsageByModel,
} from '@/hooks';

import { useComputeUsageByModel } from '@/workers';

import {
  EnergyUsageDataKey,
  getEnergyUsageInputFromFilters,
  store,
} from '../store';

export const ReeferTypeBreakDownChartContainerHeight = 300;
export const ReeferTypeBreakDownChartContainerMobileHeight = 250;
export const ReeferTypeBreakDownListContainerMobileHeight = 200;
export const ReeferTypeBreakDownListContainerMaxHeight = 220;

function ReeferTypeBreakDownChart() {
  const { t } = useTranslation();
  const theme = useTheme();
  const snap = useSnapshot(store);
  const { isMobileDevice } = useBreakpoints();

  const [titleRef, { height: titleHeight }] = useMeasure();
  const { formatNumber, formatFloat } = useFormatters();

  const params = getEnergyUsageInputFromFilters(snap as typeof store);
  const [energyUsageByModelResult, refresh] = usePostEnergyUsageByModel(params);

  const models = energyUsageByModelResult.data || [];

  const dataKey = snap.viewModeDataKey as EnergyUsageDataKey;

  const totalValue = models.reduce(
    (acc, model: EnergyUsageByModel) => acc + (model[dataKey] || 0),
    0,
  );

  const data = models
    .sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0))
    .map((model, index) => ({
      id: model.modelName,
      value: Math.round(((model[dataKey] || 0) * 100) / totalValue),
      label: model.modelName,
      color: shadeHexColor(
        snap.isAveragePowerMode
          ? theme.palette.primary.main
          : theme.palette.secondary.main,
        index / models.length,
      ),
    }));

  const isLoading = energyUsageByModelResult.isLoading;

  const rootContainerHeight = !isMobileDevice
    ? ReeferTypeBreakDownChartContainerHeight
    : ReeferTypeBreakDownChartContainerMobileHeight +
      ReeferTypeBreakDownListContainerMobileHeight;

  const chartContainerHeight = !isMobileDevice
    ? ReeferTypeBreakDownChartContainerHeight
    : ReeferTypeBreakDownChartContainerMobileHeight;

  return (
    <Paper
      variant="elevation"
      sx={{
        height: `${rootContainerHeight}px`,
        width: '100%',
        borderRadius: 4,
      }}
    >
      <Stack
        ref={titleRef}
        direction="row"
        alignItems="flex-start"
        flexGrow={1}
        spacing={1}
        sx={{ pl: 3, pt: 2 }}
      >
        <Typography variant="h5">
          {t('reeferTypeBreakDownChart.title')}
        </Typography>
        <Typography variant="h5" color="GrayText">
          {snap.viewModeUnit}
        </Typography>
      </Stack>

      <Stack
        direction={isMobileDevice ? 'column' : 'row'}
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '100%',
          height: rootContainerHeight - (titleHeight || 0),
        }}
      >
        {isLoading && <CircularProgress />}

        {!isLoading && !totalValue && (
          <Typography variant="overline">{t('noData')}</Typography>
        )}

        {!isLoading && totalValue > 0 && (
          <>
            <PieChart
              height={chartContainerHeight}
              margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
              series={[
                {
                  innerRadius: 60,
                  outerRadius: 100,
                  paddingAngle: 1,
                  data,
                },
              ]}
            />

            <List
              sx={{
                width: '100%',
                overflowY: 'auto',
                maxHeight: ReeferTypeBreakDownListContainerMaxHeight,
                minWidth: 200,
                p: 0,
                m: 0,
                ml: 2,
                mr: 2,
                mb: 2,
              }}
            >
              {models.map((d) => (
                <ListItem key={d.modelName}>
                  <Stack direction="column" flexGrow={1} spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Stack direction="row" flexGrow={1} spacing={1}>
                        <Stack direction="column" flexGrow={1}>
                          <Typography variant="subtitle2">
                            {d.modelName}
                          </Typography>
                          <Typography variant="caption">
                            {formatFloat(d[dataKey] || 0)} {snap.viewModeUnit}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.grey[200],
                            p: 1,
                            borderRadius: 2,
                          }}
                          display="flex"
                          alignItems="center"
                        >
                          <Stack
                            direction="row"
                            alignItems="flex-end"
                            justifyContent="center"
                          >
                            <Typography fontWeight="bold" variant="body1">
                              {formatNumber(
                                ((d[dataKey] || 0) * 100) / totalValue,
                              )}
                            </Typography>
                            <Typography
                              sx={{ marginBottom: '2px', marginLeft: '2px' }}
                              fontSize={10}
                              color={theme.palette.grey[600]}
                            >
                              %
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Stack>
                    <Divider />
                  </Stack>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export { ReeferTypeBreakDownChart };
